import { mdiSubdirectoryArrowRight } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useCallback, useRef } from 'react';
import { OnChangeValue } from 'react-select';
import { AsyncSelect, ISelectOption } from '../../base';
import {
    CatalogFacility,
    CatalogProvider,
} from '../../../services/worker-configuration/automation/worker-automation.types';
import {
    CatalogProviderRole,
    searchCatalogFacilities,
    searchCatalogProviders,
} from '../../../services/worker-configuration';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import * as S from './configuration-rule.styles';

type EntityKind = 'billingProviderId' | 'renderingProviderId' | 'facilityId';

interface IConfigurationRuleEntityEditorProps {
    disabled?: boolean;
    entityIds: string[];
    kind: EntityKind;
    matchIndex: number;
    ruleId: string;
    store: WorkerStore;
}

function getEntityCopy(kind: EntityKind): {
    hint: string;
    placeholder: string;
    role?: CatalogProviderRole;
} {
    switch (kind) {
        case 'billingProviderId':
            return {
                hint: 'Matches any of these billing providers (OR)',
                placeholder: 'Search billing providers…',
                role: 'billing',
            };
        case 'renderingProviderId':
            return {
                hint: 'Matches any of these rendering providers (OR)',
                placeholder: 'Search rendering providers…',
                role: 'rendering',
            };
        default:
            return {
                hint: 'Matches any of these facilities (OR)',
                placeholder: 'Search facilities by name…',
            };
    }
}

function toProviderOption(provider: CatalogProvider): ISelectOption {
    return {
        label: provider.name,
        value: provider.providerId,
        info: provider.meta ? ` · ${provider.meta}` : undefined,
    };
}

function toFacilityOption(facility: CatalogFacility): ISelectOption {
    return {
        label: facility.name,
        value: facility.facilityId,
        info: facility.meta ? ` · ${facility.meta}` : undefined,
    };
}

export const ConfigurationRuleEntityEditor: React.FC<IConfigurationRuleEntityEditorProps> = observer(
    ({ disabled, entityIds, kind, matchIndex, ruleId, store }) => {
        const copy = getEntityCopy(kind);
        const providerMapRef = useRef<Map<string, CatalogProvider>>(new Map());
        const facilityMapRef = useRef<Map<string, CatalogFacility>>(new Map());

        const selectedValue: ISelectOption[] = entityIds.map(id => {
            if (kind === 'facilityId') {
                const facility = store.facilityById[id] ?? facilityMapRef.current.get(id);
                return {
                    label: facility?.name ?? id,
                    value: id,
                    info: facility?.meta ? ` · ${facility.meta}` : undefined,
                };
            }

            const provider = store.providerById[id] ?? providerMapRef.current.get(id);
            return {
                label: provider?.name ?? id,
                value: id,
                info: provider?.meta ? ` · ${provider.meta}` : undefined,
            };
        });

        const loadOptions = useCallback(
            async (inputValue: string): Promise<ISelectOption[]> => {
                if (kind === 'facilityId') {
                    const facilities = await searchCatalogFacilities(inputValue, store.apiMode);
                    facilities.forEach(facility => {
                        facilityMapRef.current.set(facility.facilityId, facility);
                    });
                    store.rememberFacilities(facilities);
                    return facilities.map(toFacilityOption);
                }

                if (!copy.role) {
                    return [];
                }

                const providers = await searchCatalogProviders(inputValue, copy.role, store.apiMode);
                providers.forEach(provider => {
                    providerMapRef.current.set(provider.providerId, provider);
                });
                store.rememberProviders(providers);
                return providers.map(toProviderOption);
            },
            [copy.role, kind, store],
        );

        const handleChange = (option: OnChangeValue<ISelectOption, boolean>): void => {
            const selected = Array.isArray(option) ? option : option ? [option] : [];
            const ids = selected.map(entry => String(entry.value));

            if (kind === 'facilityId') {
                const facilities = ids
                    .map(id => facilityMapRef.current.get(id) ?? store.facilityById[id])
                    .filter((facility): facility is CatalogFacility => Boolean(facility));
                store.rememberFacilities(facilities);
                store.setConditionValues(ruleId, matchIndex, kind, ids);
                return;
            }

            const providers = ids
                .map(id => providerMapRef.current.get(id) ?? store.providerById[id])
                .filter((provider): provider is CatalogProvider => Boolean(provider));
            store.rememberProviders(providers);
            store.setConditionValues(ruleId, matchIndex, kind, ids);
        };

        return (
            <S.PayerEditor>
                <S.PayerSectionHint>
                    <Icon path={mdiSubdirectoryArrowRight} size={0.75} color="#8e9293" aria-hidden={true} />
                    {copy.hint}
                </S.PayerSectionHint>

                <AsyncSelect
                    isMulti={true}
                    isDisabled={disabled}
                    isClearable={true}
                    defaultOptions={true}
                    cacheOptions={true}
                    placeholder={copy.placeholder}
                    value={selectedValue}
                    loadOptions={loadOptions}
                    onChange={handleChange}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                    menuPosition="fixed"
                />
            </S.PayerEditor>
        );
    },
);
